import { FeatureGate } from "@/components/molecules/FeatureGate/FeatureGate";
import { GRID_SIZE } from "@/constants/gridCellLayouts";
import type { EnergyPlot } from "@/energyPlotView/types";
import { GridCountryPlot } from "@/grid/GridCountryPlot";
import { GridEmptyOptions } from "@/grid/GridEmptyOptions";
import { GridMap } from "@/grid/GridMap";
import { GridPlot } from "@/grid/GridPlot";
import { GridSortableElement } from "@/grid/GridSortableElement";
import { useCurrMapInteractionState } from "@/models/map-interaction/mapInteractionStateHooks";
import { isDisabledGrid } from "@/models/map-interaction/mapInteractionStateUtils";
import { useOverlay } from "@/overlay/overlays/default/ViewportOverlayProvider";
import { useAppDispatch } from "@/reducer";
import { useCountryPlots } from "@/reducer/CountryPlotState";
import { useEnergyPlots } from "@/reducer/EnergyPlotsState";
import { switchLayouts, useLayouts } from "@/reducer/LayoutsState";
import { type LocationTableState, useLocationTables } from "@/reducer/LocationTablesState";
import { useMaps } from "@/reducer/MapsState";
import { useNotes } from "@/reducer/NoteState";
import { usePlots } from "@/reducer/PlotsState";
import { useTab } from "@/reducer/TabsState";
import { useTephigrams } from "@/reducer/TephigramState";
import { type WeatherTableState, useWeatherTables } from "@/reducer/WeatherTablesState";
import type { Plot } from "@/reducer/client-models";
import type { CountryPlotType } from "@/reducer/client-models/CountryPlot";
import { getById, getToolByToolId } from "@/reducer/selectors/AccountSelectors";
import { classNames } from "@/utility/jsx";
import { Trans } from "@lingui/react/macro";
import { LayoutType, type NoteSchema, type TephigramSchemaOutput } from "@mm/metx-workbench.meteomatics.com";
import React, { useCallback, useEffect } from "react";
import { HighlightType, useActiveGridHighlight } from "./GridCellHighlightProvider";
import { GridEnergyPlot } from "./GridEnergyPlot";
import { GridNote } from "./GridNote/GridNote";
import { GridTephigram } from "./GridTephigram";

interface GridProps {
  tabId: number;
}

const gridDefinition = {
  display: "grid",
  gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
  gridTemplateRows: `repeat(${GRID_SIZE}, 1fr)`,
};

const GridWeatherTable = React.lazy(() =>
  import("./GridWeatherTable").then(({ GridWeatherTable }) => ({ default: GridWeatherTable })),
);

const GridMultiLocationTable = React.lazy(() =>
  import("./GridMultiLocationTable/GridMultiLocationTable").then(({ GridMultiLocationTable }) => ({
    default: GridMultiLocationTable,
  })),
);

export function Grid({ tabId }: GridProps) {
  const { data: tab, isLoading, hasError } = useTab();
  const dispatch = useAppDispatch();
  const { highlighted } = useActiveGridHighlight();
  const { showFooter, showHeader } = useOverlay();
  const plots = usePlots(tabId);
  const weatherTables = useWeatherTables(tabId);
  const locationTables = useLocationTables(tabId);
  const maps = useMaps(tabId);
  const energyPlots = useEnergyPlots(tabId);
  const layouts = useLayouts(tabId);
  const tephigrams = useTephigrams(tabId);
  const countryPlots = useCountryPlots(tabId);
  const notes = useNotes(tabId);

  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [layouts]);

  const changeLayoutPosition = useCallback(
    (sourceId: number, targetId: number) => {
      if (layouts && sourceId !== targetId) {
        dispatch(switchLayouts({ layoutId1: sourceId, layoutId2: targetId, tabId }));
      }
    },
    [layouts, tabId, dispatch],
  );

  const mapInteractionState = useCurrMapInteractionState();

  if (hasError) {
    return <Trans id="tab.fetch.failure">Failed to load tab from server</Trans>;
  }
  if (isLoading || !tab) {
    return <mm-loader size="xlarge" />;
  }

  const renderEmptyCell = (layout: (typeof layouts)[number], disabledGrid: boolean) => (
    <GridSortableElement
      id={layout.id}
      disabled={disabledGrid}
      gridCellLayout={layout.gridCellLayout}
      className={"grid__cell--empty"}
      key={`empty-${layout.id}`}
      onDrop={changeLayoutPosition}
    >
      <GridEmptyOptions layout={layout} tabId={tabId} />
    </GridSortableElement>
  );

  return (
    <div className="grid workspace-grid grid--no-gap cover-window" style={gridDefinition}>
      {!!layouts &&
        layouts.map((layout) => {
          const disabledGrid = isDisabledGrid(mapInteractionState, layout);
          if (layout.type === LayoutType.Map && layout.id_tool) {
            const map = getById(layout.id_tool, maps);

            if (map) {
              return (
                <FeatureGate
                  key={`map-gate-${map.id}`}
                  path="tools.map.enabled"
                  fallback={renderEmptyCell(layout, disabledGrid)}
                >
                  <GridSortableElement
                    id={layout.id}
                    disabled={disabledGrid}
                    gridCellLayout={layout.gridCellLayout}
                    className={classNames(
                      "grid__cell__map",
                      // Check if grid cell is positioned on bottom
                      layout.gridCellLayout.gridRowEnd === 97 ? "grid__cell__bottom" : "",
                      // Add class if footer is visible, so map can align controls
                      showFooter ? "footer-visible" : "",
                      highlighted && highlighted.type === HighlightType.MAP && highlighted.id === map.id
                        ? "highlighted"
                        : "",
                    )}
                    key={`map-${map.id}`}
                    onDrop={changeLayoutPosition}
                  >
                    <GridMap map={map} cellLayout={layout.gridCellLayout} />
                  </GridSortableElement>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.Plot && layout.id_tool) {
            const plot = getToolByToolId<Plot>(layout.id_tool, plots);
            if (plot) {
              return (
                <FeatureGate
                  key={`plot-gate-${plot.id}`}
                  path="tools.plot.enabled"
                  fallback={renderEmptyCell(layout, disabledGrid)}
                >
                  <GridSortableElement
                    id={layout.id}
                    disabled={disabledGrid}
                    gridCellLayout={layout.gridCellLayout}
                    className={classNames(
                      "grid__cell__plot",
                      layout.gridCellLayout.gridRowStart === 1 && showHeader && "extra-padding-top",
                      layout.gridCellLayout.gridRowEnd === 97 && showFooter && "extra-padding-bottom",
                      highlighted && highlighted.type === HighlightType.PLOT && highlighted.id === plot.id
                        ? "highlighted"
                        : "",
                    )}
                    key={`plot-${plot.id}`}
                    onDrop={changeLayoutPosition}
                  >
                    <GridPlot plot={plot} cellLayout={layout.gridCellLayout} />
                  </GridSortableElement>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.EnergyPlot && layout.id_tool) {
            const energyPlot = getToolByToolId<EnergyPlot>(layout.id_tool, energyPlots);
            if (energyPlot) {
              return (
                <FeatureGate
                  key={`energy-gate-${energyPlot.id}`}
                  path="tools.energy_plot.enabled"
                  fallback={renderEmptyCell(layout, disabledGrid)}
                >
                  <GridSortableElement
                    id={layout.id}
                    disabled={disabledGrid}
                    gridCellLayout={layout.gridCellLayout}
                    className={classNames(
                      "grid__cell__plot",
                      layout.gridCellLayout.gridRowStart === 1 && showHeader && "extra-padding-top",
                      layout.gridCellLayout.gridRowEnd === 97 && showFooter && "extra-padding-bottom",
                      highlighted && highlighted.type === HighlightType.ENERGY_PLOT && highlighted.id === energyPlot.id
                        ? "highlighted"
                        : "",
                    )}
                    key={`energy-plot-${energyPlot.id}`}
                    onDrop={changeLayoutPosition}
                  >
                    <GridEnergyPlot energyPlot={energyPlot} cellLayout={layout.gridCellLayout} />
                  </GridSortableElement>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.WeatherTable && layout.id_tool && weatherTables) {
            const weatherTable = getToolByToolId<WeatherTableState>(layout.id_tool, weatherTables);
            if (weatherTable) {
              return (
                <FeatureGate
                  key={`weather-table-gate-${weatherTable.id}`}
                  path="tools.table.enabled"
                  fallback={renderEmptyCell(layout, disabledGrid)}
                >
                  <GridSortableElement
                    id={layout.id}
                    disabled={disabledGrid}
                    gridCellLayout={layout.gridCellLayout}
                    className={classNames(
                      "grid__cell__plot",
                      layout.gridCellLayout.gridRowStart === 1 && showHeader && "extra-padding-top",
                      layout.gridCellLayout.gridRowEnd === 97 && showFooter && "extra-padding-bottom",
                      highlighted &&
                        highlighted.type === HighlightType.WEATHER_TABLE &&
                        highlighted.id === weatherTable.id
                        ? "highlighted"
                        : "",
                    )}
                    key={`weather-table-${weatherTable.id}`}
                    onDrop={changeLayoutPosition}
                  >
                    <React.Suspense fallback={null}>
                      <GridWeatherTable weatherTable={weatherTable} cellLayout={layout.gridCellLayout} />
                    </React.Suspense>
                  </GridSortableElement>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.LocationTable && layout.id_tool && locationTables) {
            const locationTable = getToolByToolId<LocationTableState>(layout.id_tool, locationTables);
            if (locationTable) {
              return (
                <FeatureGate
                  key={`location-table-gate-${locationTable.id}`}
                  path="tools.table.enabled"
                  fallback={renderEmptyCell(layout, disabledGrid)}
                >
                  <GridSortableElement
                    id={layout.id}
                    disabled={disabledGrid}
                    gridCellLayout={layout.gridCellLayout}
                    className={classNames(
                      "grid__cell__plot",
                      layout.gridCellLayout.gridRowStart === 1 && showHeader && "extra-padding-top",
                      layout.gridCellLayout.gridRowEnd === 97 && showFooter && "extra-padding-bottom",
                      highlighted &&
                        highlighted.type === HighlightType.LOCATION_TABLE &&
                        highlighted.id === locationTable.id
                        ? "highlighted"
                        : "",
                    )}
                    key={`location-table-${locationTable.id}`}
                    onDrop={changeLayoutPosition}
                  >
                    <React.Suspense fallback={null}>
                      <GridMultiLocationTable locationTable={locationTable} cellLayout={layout.gridCellLayout} />
                    </React.Suspense>
                  </GridSortableElement>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.Tephigram && layout.id_tool && tephigrams) {
            const tephigram = getToolByToolId<TephigramSchemaOutput>(layout.id_tool, tephigrams);
            if (tephigram) {
              return (
                <FeatureGate
                  key={`tephigram-gate-${tephigram.id}`}
                  path="tools.skewt.enabled"
                  fallback={renderEmptyCell(layout, disabledGrid)}
                >
                  <GridSortableElement
                    id={layout.id}
                    disabled={disabledGrid}
                    gridCellLayout={layout.gridCellLayout}
                    className={classNames(
                      "grid__cell__plot",
                      layout.gridCellLayout.gridRowStart === 1 && showHeader && "extra-padding-top",
                      layout.gridCellLayout.gridRowEnd === 97 && showFooter && "extra-padding-bottom",
                      highlighted && highlighted.type === HighlightType.TEPHIGRAM && highlighted.id === tephigram.id
                        ? "highlighted"
                        : "",
                    )}
                    key={`tephigram-${tephigram.id}`}
                    onDrop={changeLayoutPosition}
                  >
                    <GridTephigram tephigram={tephigram} cellLayout={layout.gridCellLayout} />
                  </GridSortableElement>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.CountryPlot && layout.id_tool && countryPlots) {
            const countryPlot = getToolByToolId<CountryPlotType>(layout.id_tool, countryPlots);
            if (countryPlot) {
              return (
                <FeatureGate
                  key={`country-plot-gate-${countryPlot.id}`}
                  path="tools.country_forecast.enabled"
                  fallback={renderEmptyCell(layout, disabledGrid)}
                >
                  <GridSortableElement
                    id={layout.id}
                    disabled={disabledGrid}
                    gridCellLayout={layout.gridCellLayout}
                    className={classNames(
                      "grid__cell__plot",
                      layout.gridCellLayout.gridRowStart === 1 && showHeader && "extra-padding-top",
                      layout.gridCellLayout.gridRowEnd === 97 && showFooter && "extra-padding-bottom",
                      highlighted &&
                        highlighted.type === HighlightType.COUNTRY_PLOT &&
                        highlighted.id === countryPlot.id
                        ? "highlighted"
                        : "",
                    )}
                    key={`countryPlot-${countryPlot.id}`}
                    onDrop={changeLayoutPosition}
                  >
                    <GridCountryPlot countryPlot={countryPlot} cellLayout={layout.gridCellLayout} />
                  </GridSortableElement>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.Notes && layout.id_tool && notes) {
            const note = getToolByToolId<NoteSchema>(layout.id_tool, notes);
            if (note) {
              return (
                <FeatureGate
                  key={`note-gate-${note.id}`}
                  path="tools.notes.enabled"
                  fallback={renderEmptyCell(layout, disabledGrid)}
                >
                  <GridSortableElement
                    id={layout.id}
                    disabled={disabledGrid}
                    gridCellLayout={layout.gridCellLayout}
                    className={classNames(
                      "grid__cell__plot",
                      layout.gridCellLayout.gridRowStart === 1 && showHeader && "extra-padding-top", //TODO clarify for remove
                      layout.gridCellLayout.gridRowEnd === 97 && showFooter && "extra-padding-bottom",
                      highlighted && highlighted.type === HighlightType.NOTE && highlighted.id === note.id
                        ? "highlighted"
                        : "",
                    )}
                    key={`note-${note.id}`}
                    onDrop={changeLayoutPosition}
                  >
                    <GridNote note={note} />
                  </GridSortableElement>
                </FeatureGate>
              );
            }
          }

          return renderEmptyCell(layout, disabledGrid);
        })}
    </div>
  );
}
